import { Injectable, inject } from '@angular/core';
import { CookieRepository, CookieOptions } from '@service/cookie.repository';

/**
 * Servicio especializado para gestión de tokens JWT
 * Proporciona métodos para guardar, recuperar y limpiar tokens JWT de las cookies
 */
@Injectable({
  providedIn: 'root'
})
export class JwtTokenService {
  private readonly cookieService = inject(CookieRepository);
  private readonly TOKEN_COOKIE_NAME = 'auth_token';
  private readonly TOKEN_EXPIRY_DAYS = 7;

  /**
   * Guarda el token JWT con información de auditoría del usuario
   * @param token Token JWT a guardar
   * @param auditInfo Información del usuario (userId, userName, metadata)
   * @param expiryDays Días de expiración (default: 7)
   */
  saveTokenWithAudit(
    token: string,
    auditInfo: {
      userId?: string;
      userName?: string;
      metadata?: Record<string, any>;
    },
    expiryDays: number = this.TOKEN_EXPIRY_DAYS
  ): void {
    const expiresDate = new Date();
    expiresDate.setDate(expiresDate.getDate() + expiryDays);

    const cookieOptions: CookieOptions = {
      path: '/',
      expires: expiresDate,
      secure: true,
      sameSite: 'Strict',
      // Información de auditoría
      userId: auditInfo.userId,
      userName: auditInfo.userName,
      userAgent: navigator.userAgent,
      timestamp: Date.now(),
      metadata: auditInfo.metadata
    };

    this.cookieService.setCookie(this.TOKEN_COOKIE_NAME, token, cookieOptions);
    console.log(
      '✅ Token JWT guardado con auditoría:',
      `Usuario: ${auditInfo.userName || 'unknown'} (${auditInfo.userId || 'no-id'})`
    );
  }

  /**
   * Guarda el token JWT en una cookie segura
   * @param token Token JWT a guardar
   * @param expiryDays Días de expiración (default: 7)
   */
  saveToken(token: string, expiryDays: number = this.TOKEN_EXPIRY_DAYS): void {
    const expiresDate = new Date();
    expiresDate.setDate(expiresDate.getDate() + expiryDays);

    const cookieOptions: CookieOptions = {
      path: '/',
      expires: expiresDate,
      secure: true,
      sameSite: 'Strict'
    };

    this.cookieService.setCookie(this.TOKEN_COOKIE_NAME, token, cookieOptions);
    console.log(
      '✅ Token JWT guardado en cookies:',
      `${token.substring(0, 20)}...`
    );
  }

  /**
   * Obtiene el token JWT de las cookies
   * @returns Token JWT o undefined si no existe
   */
  getToken(): string | undefined {
    const token = this.cookieService.getCookie(this.TOKEN_COOKIE_NAME);
    if (token) {
      console.log(
        '✓ Token JWT recuperado de cookies:',
        `${token.substring(0, 20)}...`
      );
    }
    return token;
  }

  /**
   * Verifica si existe un token válido en las cookies
   * @returns true si existe un token
   */
  hasToken(): boolean {
    return this.cookieService.exists(this.TOKEN_COOKIE_NAME);
  }

  /**
   * Obtiene el token completo (sin acortar para logs)
   * @returns Token completo
   */
  getFullToken(): string | undefined {
    return this.cookieService.getCookie(this.TOKEN_COOKIE_NAME);
  }

  /**
   * Elimina el token JWT (típicamente al logout)
   */
  clearToken(): void {
    this.cookieService.deleteCookie(this.TOKEN_COOKIE_NAME);
    console.log('🗑️ Token JWT eliminado de cookies');
  }

  /**
   * Decodifica el payload de un token JWT (sin verificación de firma)
   * ⚠️ Solo para lectura, NO valida la firma
   * @param token Token JWT a decodificar
   * @returns Payload decodificado o null
   */
  decodeToken(token?: string): any {
    try {
      const tokenToDecode = token || this.getToken();
      if (!tokenToDecode) {
        console.warn('❌ No hay token para decodificar');
        return null;
      }

      const parts = tokenToDecode.split('.');
      if (parts.length !== 3) {
        console.warn('❌ Token JWT inválido');
        return null;
      }

      const decoded = JSON.parse(atob(parts[1]));
      console.log('📖 Token decodificado:', decoded);
      return decoded;
    } catch (error) {
      console.error('❌ Error al decodificar token:', error);
      return null;
    }
  }

  /**
   * Verifica si el token ha expirado
   * @param token Token JWT (optional, usa el guardado si no se proporciona)
   * @returns true si el token ha expirado
   */
  isTokenExpired(token?: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded || !decoded.exp) {
        return true;
      }

      const expiryTime = decoded.exp * 1000; // Convertir a milisegundos
      const now = Date.now();
      const isExpired = now > expiryTime;

      if (isExpired) {
        console.warn('⏰ Token ha expirado');
      } else {
        const minutesLeft = Math.floor((expiryTime - now) / 60000);
        console.log(`⏱️ Token expira en ${minutesLeft} minutos`);
      }

      return isExpired;
    } catch (error) {
      console.error('❌ Error al verificar expiración:', error);
      return true;
    }
  }

  /**
   * Obtiene la información del usuario del token (claims)
   * @returns Claims del usuario o null
   */
  getUserClaims(): any {
    const decoded = this.decodeToken();
    return decoded ? { sub: decoded.sub, email: decoded.email, roles: decoded.roles } : null;
  }
}
